import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';

import {
  FontSizes,
  Radius,
  SearchInput,
  Sizes,
  Spacing,
} from 'chatie-design-system';
import { Close, Icon } from 'chatie-design-system/icon';
import { useDebounce } from 'use-debounce';

import { PaginationIterator } from '@components/common/Pagination';

import { getCharacterRecommendTags, getCharacterTags } from '@api/character';
import { FORBIDDEN_WORD } from '@base/constants/text';
import { useKeyPaginationQuery } from '@utils/query';

import { OnClickProps, formatComma } from '../../utils/misc';
import Empty from '../common/Empty';
import FullscreenModal, {
  FullscreenModalButton,
  FullscreenModalProps,
} from '../common/FullscreenModal';
import Loading from '../common/Loading';
import Selectable from '../common/Selectable';
import Content, { OuterContent } from '../layout/Content';

const subtitleCSS = css`
  font-size: ${FontSizes.small}px;
  margin-top: ${Spacing.medium}px;
  margin-bottom: ${Spacing.xlarge}px;
`;

interface TagListProps extends Pick<FullscreenModalProps, 'open' | 'name'> {
  selectedTags: string[];
  onSelectTag: (e: React.SyntheticEvent) => void;
}

interface QTagListProps extends TagListProps {
  q: string;
}

const qTagCSS = (theme: Theme) =>
  css`
    display: flex;
    align-items: center;

    h5:before {
      content: '#';
    }

    small {
      display: block;
      font-size: ${FontSizes.xsmall}px;
      margin-top: ${Spacing.xxxsmall}px;
      color: ${theme.var.gray500};
    }
  `;

const searchTagListCSS = css`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.small}px;
`;

const CharacterSearchTag = ({
  name,
  count,
  onClick,
}: { name: string; count?: number } & OnClickProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t } = useTranslation();

  return (
    <div css={qTagCSS} data-name={name} onClick={onClick}>
      <div>
        <h5 className="ellipsis">{name}</h5>
        <small>{t(`캐릭터 ${formatComma(count as number)}`)}</small>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QTagList = ({ q, open, selectedTags, onSelectTag }: QTagListProps) => {
  const { t } = useTranslation();

  const searchTagQuery = useKeyPaginationQuery(
    ['searchTags', q],
    ({ pageParam }) => getCharacterTags({ name: q }, pageParam)
  );

  const { data: recommendedTags } = useQuery(
    ['recommendTags'],
    getCharacterRecommendTags
  );

  return (
    <>
      <p css={subtitleCSS}>
        {t('캐릭터와 어울리는 태그를 선택해 주세요.(최대 10개)')}
      </p>

      {q ? (
        <>
          <PaginationIterator
            name="search-tag-list"
            query={searchTagQuery}
            margin
          >
            {(item) => (
              <Selectable
                selected={!!selectedTags.find((t) => t === item.name)}
                pad={Spacing.small}
              >
                <CharacterSearchTag
                  name={item.name}
                  count={item.tagged_character_count}
                  onClick={onSelectTag}
                />
              </Selectable>
            )}
          </PaginationIterator>
          <Empty query={searchTagQuery}>
            <p>{t('검색 결과가 없습니다.')}</p>
            <p>
              {t('검색창에 태그를 입력한 후 엔터를 눌러 태그를 생성하세요.')}
            </p>
          </Empty>
        </>
      ) : (
        <div css={searchTagListCSS}>
          {recommendedTags?.map((item) => (
            <Selectable
              selected={!!selectedTags.find((t) => t === item.name)}
              pad={Spacing.small}
            >
              <CharacterSearchTag
                name={item.name}
                count={item.tagged_character_count}
                onClick={onSelectTag}
              />
            </Selectable>
          ))}
        </div>
      )}
    </>
  );
};

const tagCSS = (theme: Theme, height?: number) => css`
  display: inline-block;
  overflow: hidden;
  padding: 0 ${Spacing.xsmall}px;
  height: ${height ?? 36}px;
  border: 1px solid ${theme.border.light};
  border-radius: ${Radius.small}px;
  margin-top: ${Spacing.xsmall}px;
  margin-right: ${Spacing.xsmall}px;
  font-size: ${FontSizes.small}px;
  width: auto;
  white-space: nowrap;

  span::before {
    content: '#';
  }

  i {
    margin-left: ${Spacing.xxsmall}px;
  }

  &:last-of-type {
    margin-right: 0;
  }
`;

interface CharacterTagProps {
  edit?: boolean;
  name: string;
  height?: number;
}

export const CharacterTag = ({
  name,
  edit,
  onClick,
  height,
}: CharacterTagProps & OnClickProps) => {
  const theme = useTheme();

  return (
    <button
      css={tagCSS(theme, height)}
      type="button"
      className="icon-text"
      data-name={name}
      onClick={onClick}
    >
      <span>{name}</span>
      {edit && (
        <Icon icon={Close} size={Sizes.small} color={theme.var.gray500} />
      )}
    </button>
  );
};

const characterTagListCSS = (isScrollable: boolean) => css`
  word-break: keep-all;

  ${isScrollable &&
  css`
    white-space: nowrap;
    overflow: auto;
    padding: 0 ${Spacing.medium}px;
    margin: 0 -${Spacing.medium}px;
  `}
`;

type ITagListProps = {
  tags: string[];
  scroll?: boolean;
  webView?: boolean;
};

export const TagList = ({ tags, scroll = false }: ITagListProps) => {
  return (
    <div css={characterTagListCSS(scroll)}>
      {tags.map((tag) => (
        <CharacterTag key={`tag-${tag}`} name={tag} />
      ))}
    </div>
  );
};

const tagListCSS = (theme: Theme) => css`
  padding: ${Spacing.xsmall}px ${Spacing.medium}px ${Spacing.medium}px;
  border-bottom: 1px solid ${theme.border.light};
`;

interface Props extends Omit<FullscreenModalProps, 'title'> {
  tags: string[];
  onSelect: (tags: string[]) => void;
}

const CharacterTagSelectionModal = ({ onSelect, ...props }: Props) => {
  const { open, name, tags, onClose } = props;

  const theme = useTheme();

  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [dQ, dQControl] = useDebounce(q, 300);

  const queryClient = useQueryClient();

  const [selectedTags, setSelectedTags] = useState<string[]>(tags ?? []);

  const inputRef = useRef<HTMLInputElement>(null);

  const clearQ = useCallback(() => {
    setQ('');
    dQControl.cancel();
  }, []);

  function handleCreateTag(e: React.SyntheticEvent) {
    e.preventDefault();

    if (selectedTags.length >= 10) {
      alert(t('태그는 총 10개까지 등록 가능합니다.'));
      return;
    }

    const q = (e.currentTarget as HTMLFormElement).q.value as string;
    if (!q) {
      return;
    }

    if (q.length > 20) {
      alert('최대 20글자까지 태그 생성이 가능합니다.');
      return;
    }

    if (selectedTags.some((selectedTag) => selectedTag === q)) {
      alert('이미 등록된 태그입니다');
      return;
    }

    for (const keyword of FORBIDDEN_WORD) {
      if (q.includes(keyword)) {
        return alert('해당 태그는 생성이 불가능합니다.');
      }
    }

    setSelectedTags(selectedTags.concat([q]));
    clearQ();
  }

  function handleSelectTag(e: React.SyntheticEvent) {
    const { dataset } = e.currentTarget as HTMLDivElement;
    const name = dataset.name as string;

    if (selectedTags.some((tag) => tag === name)) {
      setSelectedTags(selectedTags.filter((tag) => tag !== name));
      return;
    }

    if (selectedTags.length >= 10) {
      alert(t('태그는 총 10개까지 등록 가능합니다.'));
      return;
    }

    setSelectedTags(selectedTags.concat([name]));
  }

  function handleRemoveTag(e: React.SyntheticEvent) {
    const { dataset } = e.currentTarget as HTMLButtonElement;
    const name = dataset.name as string;

    setSelectedTags(selectedTags.filter((tag) => tag !== name));
  }

  function submit() {
    onSelect(selectedTags);
    onClose();
  }

  useEffect(() => {
    setSelectedTags(tags);
  }, [tags]);

  useEffect(() => {
    if (!open) {
      queryClient.invalidateQueries(['searchTags', q]);
      setSelectedTags(tags);
      clearQ();
    }
  }, [queryClient, open]);

  return (
    <FullscreenModal
      name="my-story-tag-select"
      {...props}
      title={t('태그 선택')}
      renderButton={() => (
        <FullscreenModalButton
          text={t('등록하기')}
          disabled={selectedTags.length === 0}
          onClick={submit}
        />
      )}
    >
      <Content>
        <form onSubmit={handleCreateTag}>
          <SearchInput
            ref={inputRef}
            name="q"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            onClear={() => {
              inputRef.current?.focus();
              clearQ();
            }}
            placeholder={t(
              '캐릭터와 어울리는 태그를 검색해 보세요. (최대 10개)'
            )}
          />
        </form>

        <OuterContent>
          {selectedTags.length ? (
            <div css={tagListCSS(theme)}>
              {selectedTags.map((tag) => (
                <CharacterTag
                  key={`${name}-selected-tag-${tag}`}
                  name={tag}
                  edit
                  onClick={handleRemoveTag}
                />
              ))}
            </div>
          ) : null}
        </OuterContent>

        <Suspense fallback={<Loading push />}>
          <QTagList
            {...props}
            q={dQ}
            selectedTags={selectedTags}
            onSelectTag={handleSelectTag}
          />
        </Suspense>
      </Content>
    </FullscreenModal>
  );
};

export default CharacterTagSelectionModal;
